import React, {
  useEffect,
  useRef,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react"
import { useState } from "react"
import cx from "classnames"

import { CheckIcon, Icon, IconSources } from "../../Icon"

import { FormElement } from "../form"
import {
  Combobox,
  createListCollection,
  Portal,
  useCombobox,
} from "@ark-ui/react"
import { Hint } from "../Hint"
import { nanoid } from "nanoid"

export type AutocompleteItemInput =
  | string
  | {
      [x: string]: any
      icon?: IconSources
      label?: string
      value: string
      disabled?: boolean
    }

export type AutocompleteItem = {
  [x: string]: any
  icon?: IconSources
  label: string
  value: string
  disabled?: boolean
}

export type AutocompleteRefType = {
  query: string
  input: HTMLInputElement
  open: () => void
  focus: () => void
  clearValue: () => void
  setSelectedItem: (item: AutocompleteItemInput) => void
}

type Props = {
  className?: string
  label?: string
  hint?: string
  fullWidth?: boolean
  icon?: IconSources
  items: AutocompleteItemInput[]
  popoverClassName?: string
  minQueryLength?: number
  autoFilter?: boolean
  focusOnInit?: boolean
  autocomplete?: boolean
  autocompleteRef?: React.RefObject<HTMLDivElement>
  onClearValue?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  onQueryChange?: (term: string) => void
  onSelectItem?: (item: AutocompleteItem, term: string) => boolean
  renderItem?: (item: AutocompleteItem, term: string) => React.ReactNode
  allowCustomValue?: boolean
} & FormElement &
  React.InputHTMLAttributes<HTMLInputElement>

function sanitizeItem(item: AutocompleteItemInput) {
  if (typeof item === "string") {
    // If the item is a string, create an object with label and value being the string
    return { label: item, value: item }
  } else {
    // If the item is an object, ensure it has a label property
    const { value, label } = item
    return { ...item, label: label ?? value }
  }
}

function sanitizeItems(items: AutocompleteItemInput[]) {
  return items.map((item) => sanitizeItem(item))
}

const HIGHLIGHT_CLASSNAME = "autocomplete_matching_query"
const regExpEscape = (s: string) => s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&")

type AutocompleteMatchHighlightPropsType = {
  label: string
  query: string
  matchClassName?: string
}

export const AutocompleteMatchHighlight = ({
  label,
  query,
  matchClassName = HIGHLIGHT_CLASSNAME,
}: AutocompleteMatchHighlightPropsType) => {
  if (query.trim().length === 0) return label

  const queryRX = RegExp(`(${regExpEscape(query)})`, "gi")

  return (
    <>
      {label.split(queryRX).map((part, i) =>
        part.match(queryRX) ? (
          <span className={cx(matchClassName)} key={i}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  )
}

const KEY_CODE_ENTER = 13

const ignoreItemValue = nanoid()

const Autocomplete = forwardRef(
  (
    {
      id,
      className,
      label,
      hint,
      fullWidth,
      icon,
      status,
      focusOnInit = false,
      popoverClassName,
      minQueryLength = 1,
      onQueryChange,
      onSelectItem,
      renderItem,
      items,
      onKeyDown,
      disabled,
      defaultValue,
      onClearValue,
      allowCustomValue = true,
      ...inputProps
    }: Props,
    ref
  ) => {
    const [defaultInputValue, setDefaultInputValue] = useState(defaultValue)
    const [autocompleteKey, setAutocompleteKey] = useState(true)

    const isInputValidChangedProgrammatically = useRef(false)
    const sanitizedInitialItems = useMemo(() => sanitizeItems(items), [items])

    const [value, setValue] = useState<string>((defaultValue as string) || "")
    const [popoverItems, setPopoverItems] = useState(sanitizedInitialItems)

    const collection = useMemo(
      () =>
        createListCollection({
          items: popoverItems.length
            ? popoverItems
            : sanitizeItems([{ value: ignoreItemValue }]), // having an empty collection makes the component buggy. Best practice is to always have an "instruction" item when empty. This dummy item will not be displayed
        }),
      [popoverItems]
    )

    const handleInputChange = (details: Combobox.InputValueChangeDetails) => {
      if (isInputValidChangedProgrammatically.current) {
        isInputValidChangedProgrammatically.current = false
        return
      }

      onQueryChange
        ? onQueryChange(details.inputValue)
        : setPopoverItems(
            sanitizedInitialItems.filter((item) =>
              item.label
                .toLowerCase()
                .includes(details.inputValue.toLowerCase())
            )
          )
    }

    useEffect(() => {
      setPopoverItems(sanitizeItems(items))
    }, [items])

    const inputRef = useRef<HTMLInputElement>(null)

    const combobox = useCombobox({
      ...(id ? { id } : {}),
      allowCustomValue: allowCustomValue,
      autoFocus: focusOnInit,
      value: [value],
      collection: collection,
      onInputValueChange: handleInputChange,
      openOnClick: minQueryLength === 0 && collection.items.length > 0,
      onValueChange: (details) => {
        isInputValidChangedProgrammatically.current = true
        details.items[0]
          ? setValue((details.items[0] as AutocompleteItem).value)
          : setValue("")
      },
      onInteractOutside: () => {
        setTimeout(() => inputRef.current?.blur(), 30)
      },
      positioning: {
        gutter: -3,
        flip: false,
      },
    })

    useImperativeHandle(ref, (): AutocompleteRefType => {
      return {
        query: combobox.inputValue,
        input: inputRef.current as HTMLInputElement,
        open: () => {
          combobox.setOpen(true)
        },
        focus: () => {
          combobox.focus()
        },
        clearValue: () => {
          combobox.clearValue()
          setValue("")
          setDefaultInputValue("")
          onClearValue && onClearValue()
          onQueryChange && onQueryChange("")
        },
        setSelectedItem: (selectedItem: AutocompleteItemInput) => {
          isInputValidChangedProgrammatically.current = true
          const sanitizedItem = sanitizeItem(selectedItem)
          const existingItem = popoverItems.find(
            (item) => item.value === sanitizedItem.value
          )

          if (!existingItem) {
            combobox.setOpen(false)
            setPopoverItems([])
            setDefaultInputValue(sanitizedItem.value)
            setValue(sanitizedItem.value)
            setAutocompleteKey(!autocompleteKey)
          } else {
            setValue(sanitizedItem.value)
            combobox.setOpen(false)
          }
        },
      }
    }, [combobox])

    useEffect(() => {
      focusOnInit && combobox.setOpen(collection.items.length > 0)
    }, [])

    const validItems = collection.items.filter(
      (item) => item.value !== ignoreItemValue
    )

    useEffect(() => {
      validItems.length === 0 && combobox.setOpen(false)

      combobox.focused &&
        collection.items.length > 0 &&
        !combobox.open &&
        combobox.setOpen(true)
    }, [collection.items.length])

    const autocomplete = (
      <Combobox.RootProvider
        value={combobox}
        className={cx("cobalt-Autocomplete", className, {
          "cobalt-Autocomplete--empty": validItems.length === 0,
        })}
        key={"" + autocompleteKey}
      >
        {label && (
          <Combobox.Label className="cobalt-FormField__Label">
            {label}
          </Combobox.Label>
        )}
        <Combobox.Control
          className={cx("cobalt-TextField", {
            "cobalt-TextField--error": status === "error",
            "cobalt-TextField--success": status === "success",
            "cobalt-TextField--withIcon": icon,
            "cobalt-TextField--withValue":
              inputRef.current && inputRef.current.value.length > 0,
          })}
        >
          <Combobox.Input
            className="cobalt-TextField__Input"
            ref={inputRef}
            disabled={disabled}
            defaultValue={defaultInputValue}
            {...inputProps}
            onKeyDown={(e) => {
              onKeyDown && onKeyDown(e)
              if (
                onSelectItem &&
                e.keyCode === KEY_CODE_ENTER &&
                combobox.highlightedItem
              ) {
                const processSelection = onSelectItem(
                  combobox.highlightedItem as AutocompleteItem,
                  combobox.inputValue
                )

                if (!processSelection) {
                  e.preventDefault()
                  e.stopPropagation()
                }
              }
            }}
          />
          {icon && (
            <Icon
              source={icon}
              color="accent"
              className="cobalt-TextField__Icon"
            />
          )}
          {value.length > 0 && !disabled && (
            <Combobox.ClearTrigger
              className="cobalt-Autocomplete__clear-button"
              role="clear"
              onClick={() => {
                setDefaultInputValue("")
                setValue("")
                setAutocompleteKey(!autocompleteKey)
                minQueryLength === 0 &&
                  setTimeout(() => {
                    inputRef.current?.click()
                  }, 0)
                onClearValue && onClearValue()
                onQueryChange && onQueryChange("")
              }}
            >
              <Icon source="close" size={16} />
            </Combobox.ClearTrigger>
          )}
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner className="cobalt-Autocomplete__positioner">
            <Combobox.Content
              className={cx("cobalt-Autocomplete__content", popoverClassName, {
                "cobalt-Autocomplete__content--empty": validItems.length === 0,
              })}
            >
              {validItems.map((item: AutocompleteItem, index: number) => (
                <Combobox.Item
                  key={index}
                  item={item}
                  className="cobalt-Autocomplete__item"
                >
                  {renderItem ? (
                    <div
                      onClick={(e) => {
                        if (onSelectItem) {
                          const processSelection = onSelectItem(
                            item,
                            combobox.inputValue
                          )

                          if (!processSelection) {
                            e.preventDefault()
                            e.stopPropagation()
                          }
                        }
                      }}
                    >
                      {renderItem(item, combobox.inputValue)}
                    </div>
                  ) : (
                    <div
                      className={cx("cobalt-Autocomplete__item-wrapper", {
                        "cobalt-Autocomplete__item-wrapper--disabled":
                          item.disabled,
                      })}
                    >
                      {icon && (
                        <span className="cobalt-Autocomplete__item-icon">
                          <Icon source={icon} color="accent" />
                        </span>
                      )}
                      <Combobox.ItemText className="cobalt-Autocomplete__item-label">
                        {item.label}
                      </Combobox.ItemText>
                      <Combobox.ItemIndicator className="cobalt-Autocomplete_selected-item-indicator">
                        <CheckIcon size={16} />
                      </Combobox.ItemIndicator>
                    </div>
                  )}
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.RootProvider>
    )

    return label || hint ? (
      <div
        className={cx("cobalt-FormField", {
          "cobalt-FormField--withHint": hint,
          "cobalt-FormField--fullWidth": fullWidth,
        })}
      >
        {autocomplete}
        {hint && (
          <Hint status={status}>
            <span dangerouslySetInnerHTML={{ __html: hint }} />
          </Hint>
        )}
      </div>
    ) : (
      <>{autocomplete}</>
    )
  }
)

Autocomplete.displayName = "Autocomplete"

export { Autocomplete }
